import React, { Component } from "react";
import { Button, Col, Row, Table } from "reactstrap";
import { Form } from "react-redux-form";
import {
  calcAmortPrincipal,
  annuity,
} from "../shared/financialFunctions";
import './RetirementSimulatorComponent.css'
import NumberFormat from "react-number-format";
import Switch from "react-switch";
import ReactToPrint from "react-to-print";
import ReactTooltip from "react-tooltip";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import ResultsTableBody from "./ResultTableComponent";
import { simulationParameters } from "../shared/simulationParameters";
import SimulationParameterComponent from "./SimulationParameterComponent";
import ToggleSwitch from "../components/utility/ToggleComponent";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          background: "white",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ margin: 0 }}>
          <strong>Year:</strong> {label}
        </p>

        {payload.map((p, i) => (
          <p key={i} style={{ margin: 0 }}>
            <span
              style={{
                display: "inline-block",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: p.color || "#2c3e50",
                marginRight: "8px",
              }}
            />
            <strong style={{ color: p.color || "#2c3e50" }}>
              {p.name.replace("_", " ")}:
            </strong>{" "}
            {new Intl.NumberFormat("en-US").format(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function SimulatorActionIcon({ name }) {
  switch (name) {
    case "results":
      return (
        <svg viewBox="0 0 24 24" className="simulator-action-icon" aria-hidden="true">
          <circle cx="12" cy="12" r="3.5" />
          <path d="M12 2.8v3.1" />
          <path d="M12 18.1v3.1" />
          <path d="M2.8 12h3.1" />
          <path d="M18.1 12h3.1" />
          <path d="M5.5 5.5l2.2 2.2" />
          <path d="M16.3 16.3l2.2 2.2" />
          <path d="M18.5 5.5l-2.2 2.2" />
          <path d="M7.7 16.3l-2.2 2.2" />
        </svg>
      );
    case "clear":
      return (
        <svg viewBox="0 0 24 24" className="simulator-action-icon" aria-hidden="true">
          <path d="M12 3.8l1.8 4.4 4.4 1.8-4.4 1.8L12 16.2l-1.8-4.4-4.4-1.8 4.4-1.8z" />
          <path d="M18.2 4.8l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
        </svg>
      );
    case "reset":
      return (
        <svg viewBox="0 0 24 24" className="simulator-action-icon" aria-hidden="true">
          <path d="M4.8 12a7.2 7.2 0 1 0 2.1-5.1" />
          <path d="M4.8 6.1v4.1h4.1" />
        </svg>
      );
    case "print":
      return (
        <svg viewBox="0 0 24 24" className="simulator-action-icon" aria-hidden="true">
          <path d="M7.2 8V3.8h9.6V8" />
          <path d="M7 17.2h10v3H7z" />
          <path d="M5.2 9.2h13.6a2 2 0 0 1 2 2v4.2H17" />
          <path d="M7 15.4H3.2v-4.2a2 2 0 0 1 2-2" />
        </svg>
      );
    default:
      return null;
  }
}
class RetirementSimulator extends Component {
  constructor(props) {
    super(props);
    this.calculateValues = this.calculateValues.bind(this);
    this.calculateConstantSavings = this.calculateConstantSavings.bind(this);
    this.toggleProp = this.toggleProp.bind(this);

    this.formatMoneyAxis = this.formatMoneyAxis.bind(this);
    this.animateTransition = this.animateTransition.bind(this);
    this.validForm = this.validForm.bind(this);
    this.fillValues = this.fillValues.bind(this);
    this.clearValues = this.clearValues.bind(this);
    this.clearInflation = this.clearInflation.bind(this);
    this.touchValue = this.touchValue.bind(this);
    this.setReducedLineHeight = this.setReducedLineHeight.bind(this);
    this.submitCalculateForm = React.createRef();
    this.setParam = this.setParam.bind(this);
    this.state = {
      isTableOpen: false,
      isPerpetual: false,
      incomeGrowthOrder: false,
      constantValue: true,
      viewDecimals: false,
      showAdvanced: true,
      showNominal: false,
      readMoreDescription: false,
      includeInflation: false,
      showLess: true,
      reducedLineHeight: true,
      counter: 0,
      firstRun: true,
      incomeAnnualGrowthRate: 3.0,
      incomeGrowthTouched: false,
      isMobile: window.matchMedia("(max-width: 768px)").matches,
      blinkSummary: false,
      blinkTable: false,
    };
  }
  persistReducedLineHeight(value) {
    window.sessionStorage.setItem("reducedLineHeight", JSON.stringify(value));
  }
  setReducedLineHeight(value) {
    this.setState({
      ...this.state,
      reducedLineHeight: value
    }, () => this.persistReducedLineHeight(value));
  }
  //Saving methods
  calculateConstantSavings() {
    return this.state.neededYearlySave;
  }
  formatMoneyAxis(tickItem) {
    return "$" + tickItem;
  }
  toggleProp(name, dataReady = -1) {
    if (dataReady === -1) dataReady = this.state.dataReady;

    let propVal = !this.state[name];
    this.setState({
      ...this.state,
      [name]: propVal,
      dataReady: dataReady,
    });
  }
  toSafeNumber(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }
  getEffectiveInflationRate(source = this.state) {
    if (!source.includeInflation) return 0;
    return this.toSafeNumber(source.annualInflation);
  }
  getDisplayedFundTargets() {
    const realFullyUsed = this.toSafeNumber(
      this.state.realNeededAmount ?? this.state.neededAmount
    );
    const realPreserved = this.toSafeNumber(
      this.state.realNeededAmountPerpetual ?? this.state.neededAmountPerpetual
    );
    const nominalFullyUsed = this.toSafeNumber(
      this.state.retirementTargetFungibleBase ??
        this.state.retirementTargetFungibleGrowth
    );
    const nominalPreserved = this.toSafeNumber(
      this.state.retirementTargetPermanentBase ??
        this.state.retirementTargetPermanentGrowth
    );

    return {
      fullyUsed:
        this.state.includeInflation && nominalFullyUsed > 0
          ? nominalFullyUsed
          : realFullyUsed,
      preserved:
        this.state.includeInflation && nominalPreserved > 0
          ? nominalPreserved
          : realPreserved,
    };
  }
  getSavingsHeading() {
    return this.state.includeInflation
      ? "Starting Annual Savings"
      : "Annual Savings";
  }
  validForm() {
    let v = this.state;
    if (
      !isNaN(v.savingsToday) &&
      v.saveYears > 0 &&
      v.pensionYears > 0 &&
      v.currentAnualIncome > 0 &&
      v.annualPension > 0 &&
      v.annualReturnRate > 0 &&
      //(this.state.constantValue || this.state.endingAnnualSalary > 0)
      (this.state.constantValue || (!isNaN(v.incomeAnnualGrowthRate) && v.incomeAnnualGrowthRate > 0))
     )
    
      return true;
    return false;
  }

  calculateValues(values) {
    const inflationRate = this.getEffectiveInflationRate(this.state);
    let v = {
    ...this.state,
    saveYears: Number(this.state.saveYears || 0),
    pensionYears: Number(this.state.pensionYears || 0),
  };
    const adjustedPension = v.annualPension;
    const neededAmount = calcAmortPrincipal(
      adjustedPension,
      1,
      v.annualReturnRate,
      v.pensionYears
    );
    const neededAmountPerpetual = calcAmortPrincipal(
      adjustedPension,
      1,
      v.annualReturnRate,
      100000
    );
    let neededYearlySave = 0;
    let neededYearlySavePerpetual = 0;
    /*let resultingAnnualGrowthRate = 0;

    resultingAnnualGrowthRate = annualizedReturn(
      v.endingAnnualSalary,
      v.currentAnualIncome,
      v.saveYears
    );
    */
    const resultingAnnualGrowthRatePercent = Number(v.incomeAnnualGrowthRate || 0);
    const growthDecimal = resultingAnnualGrowthRatePercent / 100;

    /*const tmp1 = Math.pow(1 + v.annualReturnRate / 100, v.saveYears);
    const tmp2 = Math.pow(1 + resultingAnnualGrowthRate, v.saveYears);
    const tmp3 = v.annualReturnRate / 100 - resultingAnnualGrowthRate;*/
    const tmp1 = Math.pow(1 + v.annualReturnRate / 100, v.saveYears);
    const tmp2 = Math.pow(1 + growthDecimal, v.saveYears);
    
    // r = real return, g = income growth
    const r = v.annualReturnRate / 100;
    const g = growthDecimal;

    let tmp4;

    // Caso especial: evitar división entre 0 cuando r == g
    if (Math.abs(r - g) < 0.000001) {
      // Fórmula límite cuando r == g
      tmp4 = v.saveYears * Math.pow(1 + r, v.saveYears - 1);
    } else {
      tmp4 = (tmp1 - tmp2) / (r - g);
    }


    const tmp5 = v.savingsToday * tmp1;
    neededYearlySave = annuity(
      neededAmount - tmp5,
      1,
      v.annualReturnRate,
      v.saveYears
    );
    neededYearlySavePerpetual = annuity(
      neededAmountPerpetual - tmp5,
      1,
      v.annualReturnRate,
      v.saveYears
    );
    const percetageYearlySave = (neededYearlySave / v.currentAnualIncome) * 100;
    const percetageYearlySavePerpetual =
      (neededYearlySavePerpetual / v.currentAnualIncome) * 100;
    const neededYearlySaveGrowth = (neededAmount - tmp5) / tmp4;
    const neededYearlySavePerpetualGrowth =
      (neededAmountPerpetual - tmp5) / tmp4;
    //resultingAnnualGrowthRate *= 100;
    const percetageYearlySaveGrowth =
      (neededYearlySaveGrowth / v.currentAnualIncome) * 100;
    const percetageYearlySavePerpetualGrowth =
      (neededYearlySavePerpetualGrowth / v.currentAnualIncome) * 100;

    this.setState(
      {
        ...this.state,
        neededAmount: neededAmount,
        realNeededAmount: neededAmount,
        neededAmountPerpetual: neededAmountPerpetual,
        realNeededAmountPerpetual: neededAmountPerpetual,
        neededYearlySave: neededYearlySave,
        percetageYearlySave: percetageYearlySave,
        neededYearlySavePerpetual: neededYearlySavePerpetual,
        percetageYearlySavePerpetual: percetageYearlySavePerpetual,
        adjustedReturnRate: parseFloat(v.annualReturnRate) + inflationRate,
        //resultingAnnualGrowthRate: 0,
        //resultingAnnualGrowthRatePerpetual: 0,
        //resultingAnnualGrowthRateGrowth: resultingAnnualGrowthRate,
        //resultingAnnualGrowthRatePerpetualGrowth: resultingAnnualGrowthRate,
        resultingAnnualGrowthRate: 0,
        resultingAnnualGrowthRatePerpetual: 0,
        resultingAnnualGrowthRateGrowth: resultingAnnualGrowthRatePercent,
        resultingAnnualGrowthRatePerpetualGrowth: resultingAnnualGrowthRatePercent,

        neededYearlySaveGrowth: neededYearlySaveGrowth,
        neededYearlySavePerpetualGrowth: neededYearlySavePerpetualGrowth,
        percetageYearlySaveGrowth: percetageYearlySaveGrowth,
        percetageYearlySavePerpetualGrowth: percetageYearlySavePerpetualGrowth,
        dataReady: neededYearlySave !== 0,
        updateResultsTableBody: true,
        counter: this.state.counter + 1,
        graphicData: this.state.chartDataFungibleBase,
        firstRun: false,
      },
      () => {
        let stringState = JSON.stringify(this.state);
        window.sessionStorage.setItem("main", stringState);
        this.runSimulation("", () => {
          this.runSimulation("Growth");
        });
      }
    );

    window.sessionStorage.setItem("testtest", "testtest");
    window.sessionStorage.setItem("test2", JSON.stringify(this.state));
    ReactTooltip.rebuild();
  }
  runSimulation(type = "", callback = null) {
  console.log("Running sim...");
  const inflationRate = this.getEffectiveInflationRate();
  let per = 0;
  let bb = this.state.savingsToday;
  let bbP = this.state.savingsToday;
  let returnBB = 0;
  let returnBBP = 0;
  let savings = 0;
  let savingsP = 0;
  let pension = this.state.annualPension;
  let eb = bb;
  let ebP = bbP;
  let conditions = ["SAVING", "RETIRED"];
  let pensionYears = this.state.pensionYears;
  let yearlySave = this.state["neededYearlySave" + type];
  let yearlySavePerpetual = this.state["neededYearlySavePerpetual" + type];
  let grossSavings = this.state.savingsToday;
  let grossSavingsP = this.state.savingsToday;
  let dataFungible = [];
  let dataPermanent = [];
  let detailedDataFungible = [];
  let detailedDataPermanent = [];
  let tmpFun = this.state["detailedDataFungible" + type];
  let tmpPer = this.state["detailedDataPermanent" + type];
  let neededReal = 0;
  let neededPerpetualReal = 0;
  let resultingAnnualGrowthRate =
    this.state["resultingAnnualGrowthRate" + type];
  let inflation = 1;
  let realIncrease = 1;

  for (let condition of conditions) {
    const end = condition === "SAVING" ? this.state.saveYears : pensionYears;
    if (condition !== "SAVING") {
      neededReal = eb;
      neededPerpetualReal = ebP;
    }

    for (let i = 0; i < end; i++) {
      per++;
      bb = eb;
      bbP = ebP;

      let temp7 =
        (1 + this.state.annualReturnRate / 100) *
        (1 + inflationRate / 100) -
        1;

      if (i === 0 && condition === "SAVING") {
        temp7 = (1 + this.state.annualReturnRate / 100) * 1 - 1;
      }

      returnBB = Number(bb * temp7);
      returnBBP = Number(bbP * temp7);

      savings = Number(condition === "SAVING" ? yearlySave : 0);
      savingsP = Number(condition === "SAVING" ? yearlySavePerpetual : 0);

      pension = Number(
        condition === "RETIRED" ? this.state.annualPension * inflation : 0
      );

      eb = +bb + savings + returnBB - pension;
      ebP = +bbP + savingsP + returnBBP - pension;

      grossSavings += savings;
      grossSavingsP += savingsP;

      dataFungible.push({
        per,
        name: condition,
        Fund_Size: Math.round(eb),
        nominal: Math.round(grossSavings),
      });

      dataPermanent.push({
        per,
        name: condition,
        Fund_Size: Math.round(ebP),
        nominal: Math.round(grossSavingsP),
      });

      detailedDataFungible.push({
        savings,
        pension,
        per,
        bb,
        eb,
        returnBB,
        condition,
        realIncrease,
        inflation: inflationRate,
        annualGrowthRate: resultingAnnualGrowthRate,
      });

      detailedDataPermanent.push({
        savings: savingsP,
        pension,
        per,
        bb: bbP,
        eb: ebP,
        returnBB: returnBBP,
        condition,
        realIncrease,
        inflation: inflationRate,
        annualGrowthRate: resultingAnnualGrowthRate,
      });

      realIncrease =
        (1 + resultingAnnualGrowthRate / 100) *
        (1 + inflationRate / 100);

      yearlySave =
        yearlySave *
        (1 + resultingAnnualGrowthRate / 100) *
        (1 + inflationRate / 100);

      yearlySavePerpetual =
        yearlySavePerpetual *
        (1 + resultingAnnualGrowthRate / 100) *
        (1 + inflationRate / 100);

      inflation *= 1 + inflationRate / 100;
    }
  }

  const suffix = type || "Base";

  this.setState(
    {
      ...this.state,

      [`chartDataFungible${suffix}`]: dataFungible,
      [`chartDataPermanent${suffix}`]: dataPermanent,
      [`retirementTargetFungible${suffix}`]: neededReal,
      [`retirementTargetPermanent${suffix}`]: neededPerpetualReal,

      ["prevDetailedFungible" + type]: tmpFun,
      ["prevDetailedPermanent" + type]: tmpPer,
      ["detailedDataFungible" + type]: detailedDataFungible,
      ["detailedDataPermanent" + type]: detailedDataPermanent,
    },
    () => {
      this.animateTransition();
      if (callback) callback();
    }
  );
}

  // Qué claves de Y estás graficando (ajústalo si usas otras)
  yKeysForChart = ['Fund_Size'];

  /** Máximo en un arreglo de puntos para las claves definidas */
  getArrayMax = (arr = []) =>
    arr.reduce((mx, d) => {
      const local = this.yKeysForChart.reduce((m, k) => Math.max(m, +d[k] || 0), 0);
      return Math.max(mx, local);
    }, 0);

  /** Máximo compartido entre los dos gráficos (izq y der) */
    getSharedYAxisMax = () => {
      const datasets = [
        this.state.chartDataFungibleBase || [],
        this.state.chartDataPermanentBase || [],
        this.state.chartDataFungibleGrowth || [],
        this.state.chartDataPermanentGrowth || [],
      ];

      const max = Math.max(...datasets.map((arr) => this.getArrayMax(arr)));
      return max ? Math.ceil(max * 1.02) : 0;
    };
  
  fillValues(forReset = false) {
        const DEFAULT_GROWTH = 0;
        const DEFAULT_INFLATION = 0;
        const MAXIMUM_INFLATION = 50;
    this.setState({
      ...this.state,
      savingsToday: 10000,
      saveYears: 40,
      pensionYears: 30,
      currentAnualIncome: 40000,
      annualPension: 36000,
      annualReturnRate: 4.0,
      adjustedReturnRate: 4.0,
      //endingAnnualSalary: 80000,
      resultingAnnualGrowthRateGrowth: forReset ? 0 : DEFAULT_GROWTH,
      resultingAnnualGrowthRatePerpetualGrowth: forReset ? 0 : DEFAULT_GROWTH,

      incomeAnnualGrowthRate: "",
      incomeGrowthTouched: false,

      annualInflation: "",
      inflationTouched: false,
    });
  }
  clearValues() {
    this.setState(prev => ({
    ...prev,
    
    savingsToday: "",
    saveYears: "",
    pensionYears: "",
    currentAnualIncome: "",
    annualPension: "",
    annualReturnRate: "",
    adjustedReturnRate: "",
    //endingAnnualSalary: "",
    incomeAnnualGrowthRate: "",
    annualInflation: "",
    incomeGrowthTouched: false,
    inflationTouched: false,


  }));
    
  }
  clearInflation() {
    const infValue = this.state.includeInflation;
    this.setState({
      ...this.state,
      annualInflation: infValue ? 2.5 : 0,
    });
  }
  componentDidMount() {
    this.mediaQuery = window.matchMedia("(max-width: 768px)");

  const handler = (e) => this.setState({ isMobile: e.matches });

  this.mediaQuery.addEventListener
    ? this.mediaQuery.addEventListener("change", handler)
    : this.mediaQuery.addListener(handler);

  const isMobileNow = this.mediaQuery.matches;
  const savedReducedLineHeight = window.sessionStorage.getItem("reducedLineHeight");
  const parsedReducedLineHeight =
    savedReducedLineHeight === null
      ? undefined
      : JSON.parse(savedReducedLineHeight);

  let stringState = window.sessionStorage.getItem("main");
  if (stringState) {
    const saved = JSON.parse(stringState);
    this.setState({ ...saved, isMobile: isMobileNow,
      reducedLineHeight:
        typeof parsedReducedLineHeight === "boolean"
          ? parsedReducedLineHeight
          : (typeof saved.reducedLineHeight === "boolean"
            ? saved.reducedLineHeight
            : true),

      //quitar esto para activar "include inflation" por defecto
      includeInflation: false,
      annualInflation: 0,
     });
     
	  } else {
    this.fillValues();
    this.setState({
      isMobile: isMobileNow,
      reducedLineHeight:
        typeof parsedReducedLineHeight === "boolean"
          ? parsedReducedLineHeight
          : true,
    });
  }
  }
  animateTransition() {
    let elems = document.querySelectorAll(".animate");
    console.log("To animate " + elems.length.toString());

    [].forEach.call(elems, function (el) {
      el.animate(
        [
          // keyframes
          {
            backgroundColor: "#3498db",
          },
          {
            backgroundColor: "#ffffff",
          },
        ],
        {
          duration: 1500,
          iterations: 1,
        }
      );
    });
  }
triggerBlink = (key = "blinkSummary", ms = 220) => {
  this._blinkTimers = this._blinkTimers || {};
  const timers = this._blinkTimers;

  this.setState({ [key]: false }, () => {
    requestAnimationFrame(() => this.setState({ [key]: true }));

    if (timers[key]) clearTimeout(timers[key]);
    timers[key] = setTimeout(() => this.setState({ [key]: false }), ms);
  });
};

componentWillUnmount() {
  if (this._blinkTimer) clearTimeout(this._blinkTimer);

  // (opcional, pero recomendado) limpiar el listener del matchMedia:
  if (this.mediaQuery && this._mqHandler) {
    this.mediaQuery.removeEventListener
      ? this.mediaQuery.removeEventListener("change", this._mqHandler)
      : this.mediaQuery.removeListener(this._mqHandler);
  }
}

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }
  touchValue(name) {
    /*
    if (!this.state[name + "touched"])
      this.setState({
        ...this.state,
        [name]: "",
        [name + "touched"]: true,
      });
    */
  }
  
  setParam(name, value) {
  const numericValue = Number(value);

  if (name === "annualInflation" && numericValue > 50) {
    this.setState({ showInflationLimitModal: true });
    return;
  }

  this.setState(
    prev => ({
      ...prev,
      [name]: value,
      ...(name === "annualInflation"
        ? { inflationTouched: true }   // 👈 MARCAR CUANDO EL USUARIO ESCRIBE
        : {}),
      ...(name === "incomeAnnualGrowthRate"
        ? { incomeGrowthTouched: true }
        : {}),
    }),
    () => this.calculateValues()
  );
}
getNoIncomeGrowthSummary() {
  const {
    neededYearlySave,
    neededYearlySavePerpetual,
    percetageYearlySave,
    percetageYearlySavePerpetual,
  } = this.state;
  const displayedTargets = this.getDisplayedFundTargets();
  const savingsLabel = this.state.includeInflation
    ? "starting annual savings"
    : "annual savings";
  const savingsNote = this.state.includeInflation
    ? " This starting amount then rises with inflation each year."
    : "";
  const fundLabel = this.state.includeInflation
    ? "projected retirement fund shown in nominal dollars"
    : "required retirement fund";

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(v || 0);

  const percent = (v) => {
  const n = Number(v || 0);
  const d = this.state.viewDecimals ? 2 : 0;
  return `${n.toFixed(d)}%`;
};

  return (
    <div className="simulation-summary no-income-growth">
      <p>
        
        When income remains flat over time, the required level of retirement saving depends on whether the retirement fund is <strong>allowed to be fully used</strong> or will <strong>be preserved</strong> throughout retirement.
      </p>
      <p>
        If the fund will <strong>be fully used during retirement</strong>, the{" "}
        {fundLabel} is <strong>{money(displayedTargets.fullyUsed)}</strong>.
        Reaching this target requires {savingsLabel} of{" "}
        <strong>{money(neededYearlySave)}</strong>, which represents about{" "}
        <strong>{percent(percetageYearlySave)} of income</strong>.
        {savingsNote}
        {" "}If the fund <strong>will be preserved</strong>, the required fund
        increases to <strong>{money(displayedTargets.preserved)}</strong>. In this
        case, {savingsLabel} must rise to{" "}
        <strong>{money(neededYearlySavePerpetual)}</strong>, or about{" "}
        <strong>{percent(percetageYearlySavePerpetual)} of income</strong>.
      </p>
      <p>The table below summarizes these two outcomes, showing both the <strong>target fund size</strong> and the <strong>annual savings effort</strong> under each approach.</p>
    </div>
  );
}

getIncomeGrowthSummaryText() {
  const {
    // WITHOUT income growth
    neededYearlySave,
    neededYearlySavePerpetual,
    percetageYearlySave,
    percetageYearlySavePerpetual,

    // WITH income growth
    neededYearlySaveGrowth,
    neededYearlySavePerpetualGrowth,
    percetageYearlySaveGrowth,
    percetageYearlySavePerpetualGrowth,
  } = this.state;
  const displayedTargets = this.getDisplayedFundTargets();
  const baseSavingsLabel = this.state.includeInflation
    ? "starting annual savings"
    : "annual savings";
  const baseSavingsNote = this.state.includeInflation
    ? " This starting amount then rises with inflation each year."
    : "";
  const growthSavingsNote = this.state.includeInflation
    ? " This starting amount then rises with both income growth and inflation each year."
    : " Savings then rise with income growth over time.";
  const fundLabel = this.state.includeInflation
    ? "projected target fund shown in nominal dollars"
    : "target fund";

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number.isFinite(Number(v)) ? Number(v) : 0);

  const percent = (v) => {
  const n = Number(v || 0);
  const d = this.state.viewDecimals ? 2 : 0;
  return `${n.toFixed(d)}%`;
};

  return (
    <div className="simulation-summary">
      {/* ===================== */}
      {/* WHEN INCOME DOES NOT GROW */}
      {/* ===================== */}

      <p>
        
        <strong>When income DOES NOT grow</strong><br/>
        If your income stays flat over time, the amount you must save depends on
        whether you plan to spend down your retirement fund or preserve it.<br />
        If the fund will <strong>be fully used during retirement</strong>, you would need to build a
        fund of <strong>{money(displayedTargets.fullyUsed)}</strong>, requiring {baseSavingsLabel}
        {" "}of <strong>{money(neededYearlySave)}</strong> or about{" "}
        <strong>{percent(percetageYearlySave)}</strong> of your income.{baseSavingsNote}<br/>
        If the fund will <strong>be preserved</strong>, you would need a
        larger fund of <strong>{money(displayedTargets.preserved)}</strong>,
        requiring {baseSavingsLabel} of{" "}
        <strong>{money(neededYearlySavePerpetual)}</strong> or about{" "}
        <strong>{percent(percetageYearlySavePerpetual)}</strong> of your income.

      </p>

      {/* ===================== */}
      {/* WHEN INCOME DOES GROW */}
      {/* ===================== */}

      <p>
        <strong>When income DOES grow</strong><br/>
        If your income increases over time, the required savings effort drops
        substantially — even though the <strong>{fundLabel} remains the same</strong>.<br/>
        If the fund will <strong>be fully used during retirement</strong>, you would still need{" "}
        <strong>{money(displayedTargets.fullyUsed)}</strong>, but {baseSavingsLabel} fall to{" "}
        <strong>{money(neededYearlySaveGrowth)}</strong>, or just{" "}
        <strong>{percent(percetageYearlySaveGrowth)}</strong> of income.{growthSavingsNote}<br/>
        If the fund will <strong>be preserved</strong>, you would still need{" "}
        <strong>{money(displayedTargets.preserved)}</strong>, but {baseSavingsLabel} drop
        to <strong>{money(neededYearlySavePerpetualGrowth)}</strong>, or{" "}
        <strong>{percent(percetageYearlySavePerpetualGrowth)}</strong> of income.<br/>

      </p>

      <p style={{ fontStyle: "italic", marginTop: "0.75rem" }}>
        Note: For each fund outcome, the target fund stays the same whether income grows or not; income growth changes the savings path, not the target.
      </p>
    </div>
  );
}

getCommonEvolutionText() {
  return (
    <>
     <h4 className="simulation-projection-heading" style={{ width: "100%", textAlign: "left" }}>
      Simulation Projection - Fund Evolution Over Time
    </h4>

      <p>
        The table below shows how the retirement fund changes year by year and is
        divided into two parts. The first part shows how the fund is built during
        the saving years, including the amounts saved, the investment returns
        earned, and the resulting balance. The second part shows what happens
        after retirement, when withdrawals begin. It tracks the amounts
        withdrawn, the investment returns, and how the fund balance changes over
        time.
        <br />
        <strong>
          In the scenario where the fund is intended to be preserved, the
          balance does not decline. In this case, annual withdrawals are equal to
          the investment returns earned by the fund.
        </strong>
      </p>
    </>
  );
}

getNoIncomeGrowthSummaryMobile() {
  const {
    neededYearlySave,
    neededYearlySavePerpetual,
    percetageYearlySave,
    percetageYearlySavePerpetual,
  } = this.state;
  const displayedTargets = this.getDisplayedFundTargets();
  const savingsLabel = this.state.includeInflation
    ? "Start by saving"
    : "Save";
  const savingsSuffix = this.state.includeInflation
    ? "/year to start"
    : "/year";

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number.isFinite(Number(v)) ? Number(v) : 0);

  const percent = (v) =>
    `${(Number.isFinite(Number(v)) ? Number(v) : 0).toFixed(0)}%`;

  return (
    <div className="simulation-summary mobile no-income-growth">
      <p>
        
        If income stays flat, required savings depend on whether the retirement
        fund is <strong>spent down</strong> or <strong>kept intact</strong>.<br/>
        <strong>Fund fully used:</strong><br />
        Target fund <strong>{money(displayedTargets.fullyUsed)}</strong> → {savingsLabel}{" "}
        <strong>{money(neededYearlySave)}{savingsSuffix}</strong>{" "}
        (<strong>{percent(percetageYearlySave)}</strong> of income) <br/>
        <strong>Fund will be preserved:</strong><br />
        Target fund <strong>{money(displayedTargets.preserved)}</strong> → {savingsLabel}{" "}
        <strong>{money(neededYearlySavePerpetual)}{savingsSuffix}</strong>{" "}
        (<strong>{percent(percetageYearlySavePerpetual)}</strong> of income)<br />
        The table below summarizes the required{" "}
        <strong>fund size</strong> and{" "}
        <strong>annual savings</strong> under each option.
      </p>
    </div>
  );
}

showBeforeTableWithIGMobile() {

  return (
    <div className="simulation-summary mobile with-income-growth">
      <p><strong>What this table shows</strong>
      <br/>
      Income growth reduces how hard you need to save, even when the target fund stays the same.
      </p>
      
    </div>
      );
}

showAfterTableWithIGMobile() {

  return (
    <div className="simulation-summary mobile with-income-growth">
      <p><strong>How to interpret it</strong>
      <br/>
      Without income growth, keeping the fund intact requires a much higher share of income.<br/>
      With income growth, saving becomes easier in both cases.<br/>
      When the fund stays constant, withdrawals match returns, so the balance does not decline.
      </p>
    </div>
      );
}

formatNumber(value, decimals = 0) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";

  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
renderFundChartTitle(isPreserved) {
  return isPreserved ? "Fund will be preserved" : "Fund will be fully used";
}

buildComparisonChartData(baseData = [], growthData = []) {
  const pointsByYear = new Map();

  baseData.forEach((point) => {
    pointsByYear.set(point.per, {
      per: point.per,
      withoutIncomeGrowth: point.Fund_Size,
    });
  });

  growthData.forEach((point) => {
    const existingPoint = pointsByYear.get(point.per) || { per: point.per };
    pointsByYear.set(point.per, {
      ...existingPoint,
      withIncomeGrowth: point.Fund_Size,
    });
  });

  return Array.from(pointsByYear.values()).sort((a, b) => a.per - b.per);
}

renderComparisonChart({ title, baseData, growthData, sharedYMax, showGrowth }) {
  const chartId = title.toLowerCase().replace(/\s+/g, "-");
  const chartData = this.buildComparisonChartData(baseData, growthData);
  return (
    <>
      <h5 style={{ textAlign: "center" }}>
        {title}
        &nbsp;
        <i
          data-tip={
            title === "Fund will be preserved"
              ? "Fund will be preserved during withdrawal years."
              : "After a number of withdrawal years the fund will be fully used."
          }
          className="fa-info-circle-alt"
        >
          i
        </i>
      </h5>

      <Col hidden={!this.state.dataReady} id="resultsGraphic" md={12}>
        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <AreaChart
            data={chartData}
            style={{ fontSize: "0.8rem" }}
            margin={{ left: 10, right: 10, top: 5, bottom: 0 }}
          >
            <defs>
              <linearGradient id={`${chartId}-base-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e67e22" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#e67e22" stopOpacity={0.04} />
              </linearGradient>
              <linearGradient id={`${chartId}-growth-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1f4e79" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#1f4e79" stopOpacity={0.08} />
              </linearGradient>
            </defs>
            <Legend
              iconType="plainline"
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{ paddingTop: "8px" }}
              formatter={(value) => (
                <span style={{ color: "#2c3e50" }}>{value}</span>
              )}
            />
            <XAxis
              dataKey="per"
              type="number"
              domain={[1, Number(this.state.saveYears || 0) + Number(this.state.pensionYears || 0)]}
              ticks={this.getYearTicks()}
              allowDecimals={false}
              height={40}
              label={{
                value: "Year",
                position: "insideBottom",
                offset: -2,
                style: { textAnchor: "middle" },
              }}
            />

            <YAxis
              width={90}
              tickMargin={6}
              domain={sharedYMax > 0 ? [0, sharedYMax] : ["auto", "auto"]}
              tickFormatter={(tick) => {
                return "$" + new Intl.NumberFormat("en").format(tick);
              }}
            />

            <CartesianGrid stroke="#d7dee6" strokeDasharray="2 4" />
            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="withoutIncomeGrowth"
              name="Without Income Growth"
              stroke="#d96c10"
              strokeWidth={3}
              legendType="plainline"
              fill={`url(#${chartId}-base-fill)`}
              dot={false}
              activeDot={{ r: 4 }}
            />

            {showGrowth && (
              <Area
                type="monotone"
                dataKey="withIncomeGrowth"
                name="With Income Growth"
                stroke="#1f4e79"
                strokeWidth={3}
                legendType="plainline"
                fill={`url(#${chartId}-growth-fill)`}
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Col>
    </>
  );
}

getYearTicks = () => {
  const totalYears =
    Number(this.state.saveYears || 0) + Number(this.state.pensionYears || 0);

  if (totalYears <= 0) return [];

  const ticks = [1];

  for (let i = 2; i <= totalYears; i += 2) {
    ticks.push(i);
  }

  if (!ticks.includes(totalYears)) {
    ticks.push(totalYears);
  }

  return ticks;
};

renderNoIncomeGrowthTable() {
  const d = this.state.viewDecimals ? 2 : 0;
  const displayedTargets = this.getDisplayedFundTargets();

  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "$0";

    return n.toLocaleString(undefined, {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
      style: "currency",
      currency: "USD",
    });
  };

  const percent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0%";

    return `${n.toFixed(d)}%`;
  };

  return (
    <table className="results-table minimal">
      <colgroup>
        <col className="summary-outcome-col" />
        <col className="summary-fund-col" />
        <col className="summary-savings-col" />
        <col className="summary-income-col" />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={4} className="table-title">
            Target Retirement Fund Size and Annual Savings
          </th>
        </tr>
        <tr>
          <th colSpan={4} className="table-subtitle">
            Without income growth
          </th>
        </tr>
        <tr>
          <th>Fund Outcome</th>
          <th>Required Retirement Fund</th>
          <th>Annual Savings</th>
          <th>% Income</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>Fund will be fully used</td>
          <td>{money(displayedTargets.fullyUsed)}</td>
          <td>{money(this.state.neededYearlySave)}</td>
          <td>{percent(this.state.percetageYearlySave)}</td>
        </tr>

        <tr>
          <td>Fund will be preserved</td>
          <td>{money(displayedTargets.preserved)}</td>
          <td>{money(this.state.neededYearlySavePerpetual)}</td>
          <td>{percent(this.state.percetageYearlySavePerpetual)}</td>
        </tr>
      </tbody>
    </table>
  );
}

renderWithIncomeGrowthTable() {
  const d = this.state.viewDecimals ? 2 : 0;
  const displayedTargets = this.getDisplayedFundTargets();

  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "$0";

    return n.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  };

  const percent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0%";

    return `${n.toFixed(d)}%`;
  };

  return (
    <table className="results-table minimal">
      <colgroup>
        <col className="summary-outcome-col" />
        <col className="summary-fund-col" />
        <col className="summary-savings-col" />
        <col className="summary-income-col" />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={4} className="table-title" style={{ textAlign: "center" }}>
            Target Retirement Fund Size and Annual Savings
          </th>
        </tr>

        {/* WITHOUT income growth */}
        <tr>
          <th colSpan={4} className="table-subtitle" style={{ textAlign: "center" }}>
            Without income growth
          </th>
        </tr>
        <tr>
          <th>Fund Outcome</th>
          <th>Required Retirement Fund</th>
          <th>Annual Savings</th>
          <th>% Income</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>Fund will be fully used</td>
          <td>{money(displayedTargets.fullyUsed)}</td>
          <td>{money(this.state.neededYearlySave)}</td>
          <td>{percent(this.state.percetageYearlySave)}</td>
        </tr>

        <tr>
          <td>Fund will be preserved</td>
          <td>{money(displayedTargets.preserved)}</td>
          <td>{money(this.state.neededYearlySavePerpetual)}</td>
          <td>{percent(this.state.percetageYearlySavePerpetual)}</td>
        </tr>

        {/* WITH income growth */}
        <tr>
          <td colSpan={4} className="table-subtitle" style={{ textAlign: "center" }}>
            With income growth
          </td>
        </tr>
        <tr>
          <th>Fund Outcome</th>
          <th>Required Retirement Fund</th>
          <th>Annual Savings</th>
          <th>% Income</th>
        </tr>

        <tr>
          <td>Fund will be fully used</td>
          <td>{money(displayedTargets.fullyUsed)}</td>
          <td>{money(this.state.neededYearlySaveGrowth)}</td>
          <td>{percent(this.state.percetageYearlySaveGrowth)}</td>
        </tr>

        <tr>
          <td>Fund will be preserved</td>
          <td>{money(displayedTargets.preserved)}</td>
          <td>{money(this.state.neededYearlySavePerpetualGrowth)}</td>
          <td>{percent(this.state.percetageYearlySavePerpetualGrowth)}</td>
        </tr>
      </tbody>
    </table>
  );
}



  scrollToResults = () => {
    
      const resultsTitle = document.getElementById("ResultsTitle");
      if (resultsTitle) {
        resultsTitle.scrollIntoView({ behavior: "smooth", block: "start" });
      }
  };


  render() {
    const parameters = simulationParameters;
    const perpetuityValues = [false, true];
    const withIncomeGrowthValues = [
      this.state.incomeGrowthOrder,
      !this.state.incomeGrowthOrder,
    ];

    const growthOn = !this.state.constantValue; // Incorporate Income Growth ON
    const growthVal = Number(this.state.incomeAnnualGrowthRate);

    const growthMissing =
      growthOn &&
      (
        this.state.incomeAnnualGrowthRate === "" ||
        this.state.incomeAnnualGrowthRate === null ||
        this.state.incomeAnnualGrowthRate === undefined ||
        Number.isNaN(growthVal) ||
        growthVal === 0
      );

    const growthNeedsSpacing = growthMissing && this.state.includeInflation;

    const sharedYMax = this.getSharedYAxisMax();
    return (
      <div className={this.state.reducedLineHeight ? "simulator-reduced-line-height" : ""}>
        <Form
          model="simulationdata"
          onSubmit={(values) => this.calculateValues(values)}
        >
          <div
            className="container"
            style={{ maxWidth: "3000px" }}
            ref={(el) => (this.componentRef = el)}
          >
            <div className="row-content">
              <Row className="card-simulation-style-padding">
                <div
                  className="container-fluid card-simulation-style"
                  id="mainParametersDiv"
                >
                  <Row>
                    <Col xl={9} lg={8} md={12} sm={12} className="simulator-parameters-col" style={{ fontSize: "1rem" }}>
                      <h4>Simulation Parameters</h4>
                      <h6>Enter your own estimates or use the defaults. Any currency may be used; '$' is just a format symbol.</h6>
                      {/*{parameters.map((param) => {
                        return param.name === "endingAnnualSalary" ||
                          param.name === "annualInflation" ? ( */}
                        {parameters.map((param) => {
                          return param.name === "incomeAnnualGrowthRate" ||
                            param.name === "annualInflation" ? (
                          <React.Fragment></React.Fragment>
                        ) : (
                          <SimulationParameterComponent
                            param={param}
                            viewDecimals={this.state.viewDecimals}
                            touchValue={this.touchValue}
                            setParam={this.setParam}
                            value={this.state[param.name]}
                            
                          ></SimulationParameterComponent>
                        );
                      })}
                    {/*
                      <div
                        style={{
                          opacity: !this.state.constantValue ? "1" : "0",
                        }}
                      >
                        <SimulationParameterComponent
                          param={parameters.find(
                            (x) => x.name === "endingAnnualSalary"
                          )}
                          viewDecimals={this.state.viewDecimals}
                          touchValue={this.touchValue}
                          setParam={this.setParam}
                          value={
                            this.state[
                            parameters.find(
                              (x) => x.name === "endingAnnualSalary"
                            ).name
                            ]
                          }
                        ></SimulationParameterComponent>
                        <div class="form-group fadeIn row">
                          <label class="simulationParameterLabel col-md-9 col-form-label">
                            Income Annual Growth Rate&nbsp;
                            <i
                              data-tip="Resulting annual income growth rate"
                              class="fa-info-circle-alt"
                              currentitem="false"
                            >
                              i
                            </i>
                          </label>

                          <div class="col-md-3">
                            <input
                              model=".endingAnnualSalary"
                              id="endingAnnualSalary"
                              name="endingAnnualSalary"
                              autocomplete="off"
                              placeholder="Ending Annual Income"
                              class="form-control"
                              type="text"
                              style={{maxWidth: "120px"}}
                              value={
                                (
                                  this.state.resultingAnnualGrowthRateGrowth ??
                                  0
                                ).toFixed(2) + "%"
                              }
                              readOnly={true}
                            />
                          </div>
                        </div>
                      </div>
                    */}
                    <div className={`ig-slot ${growthNeedsSpacing ? "ig-slot-with-error" : ""}`}>
                    <div className={`ig-content ${!this.state.constantValue ? "show" : "hide"}`}>
                      <SimulationParameterComponent
                        param={parameters.find((x) => x.name === "incomeAnnualGrowthRate")}
                        viewDecimals={this.state.viewDecimals}
                        touchValue={this.touchValue}
                        setParam={this.setParam}
                        value={this.state.incomeAnnualGrowthRate}
                        invalid={growthMissing}
                      />

                      {growthMissing && (
                        <Row className="form-group fadeIn">
                          <Col md={{ size: 6, offset: 9 }}>
                            <div className="text-danger" style={{ fontSize: "0.80rem", marginTop: "-0.5rem" }}>
                              Enter a value greater than 0 to apply income growth.
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>

                      
                      <div
                        style={{
                          display: this.state.includeInflation ? "block" : "none",
                        }}
                      >
                        <SimulationParameterComponent
                          param={parameters.find(
                            (x) => x.name === "annualInflation"
                          )}
                          viewDecimals={this.state.viewDecimals}
                          touchValue={this.touchValue}
                          setParam={this.setParam}
                          value={
                            this.state[
                            parameters.find(
                              (x) => x.name === "annualInflation"
                            ).name
                            ]
                          }
                        ></SimulationParameterComponent>
                        <div
                          class="form-group fadeIn row"
                          style={{ display: "none" }}
                        >
                          <label class="simulationParameterLabel col-md-9 col-form-label">
                            Adjusted to inflation annual rate&nbsp;
                            <i
                              data-tip="Annual return rated adjusted for inflation"
                              class="fa-info-circle-alt"
                              currentitem="false"
                            >
                              i
                            </i>
                          </label>
                          <div class="col-md-3">
                            <input
                              autocomplete="off"
                              placeholder="Adjusted annual rate"
                              class="form-control"
                              type="text"
                              value={
                                (
                                  this.toSafeNumber(this.state.annualReturnRate) +
                                  this.getEffectiveInflationRate()
                                ).toFixed(2) + "%"
                              }
                              readOnly={true}
                            />
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col
                      className="form-group btn-row onMobile-buttons simulator-actions-col"
                      xl={3}
                      lg={4}
                      md={12}
                      sm={12}
                      style={{
                        paddingLeft: "20px",
                        paddingRight: "20px",
                        marginLeft: "0",
                      }}
                    >
                      <div className="simulator-actions-buttons">
                        <div className="simulator-actions-row">
                          <Button
                            type="button"
                            onClick={() => {
                              var element = document.getElementById("ResultsTitle");

                              element.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "nearest",
                              });
                            }}
                            color="primary"
                            data-tip="See results"
                            className="simulator-action-btn simulator-action-btn-primary simulator-action-btn-results"
                            hidden={!this.state.dataReady}
                          >
                            <span className="simulator-action-btn-content">
                              <SimulatorActionIcon name="results" />
                              <span className="simulator-action-btn-label">Go to results</span>
                            </span>
                          </Button>
                          <Button
                            type="button"
                            onClick={this.clearValues}
                            data-tip="Clear entries and results"
                            color="warning"
                            className="btn-custom-warning simulator-action-btn simulator-action-btn-warning simulator-action-btn-top"
                          >
                            <span className="simulator-action-btn-content">
                              <SimulatorActionIcon name="clear" />
                              <span className="simulator-action-btn-label">Clear</span>
                            </span>
                          </Button>
                        </div>

                        <div className="simulator-actions-row">
                          <Button
                            type="button"
                            onClick={this.fillValues}
                            data-tip="Default values will be reinstated"
                            color="danger"
                            className="simulator-action-btn simulator-action-btn-danger simulator-action-btn-bottom"
                          >
                            <span className="simulator-action-btn-content">
                              <SimulatorActionIcon name="reset" />
                              <span className="simulator-action-btn-label">Reset</span>
                            </span>
                          </Button>
                          <ReactToPrint
                            trigger={() => {
                              return (
                                <Button
                                  data-tip="Print/Download"
                                  color="success"
                                  type="button"
                                  className="simulator-action-btn simulator-action-btn-success simulator-action-btn-bottom simulator-action-btn-print"
                                >
                                  <span className="simulator-action-btn-content">
                                    <SimulatorActionIcon name="print" />
                                    <span className="simulator-action-btn-label">Print</span>
                                  </span>
                                </Button>
                              );
                            }}
                            content={() => this.componentRef}
                          />
                        </div>
                      </div>

                      <div className="simulator-actions-advanced" hidden={!this.state.dataReady}>
                        <h5>Advanced options:</h5>
                        <div> 
                          <Switch
                            height={15}
                            width={30}
                            onColor={"#81ecec"}
                            offColor={"#81ecec"}
                            onChange={() => {
                              this.setState(
                                (prev) => {
                                  const nextConstantValue = !prev.constantValue;
                                  const turningOn = prev.constantValue && !nextConstantValue;

                                  const newState = {
                                    ...prev,
                                    constantValue: nextConstantValue,
                                  };

                                  // Si se está activando y NO se ha ingresado un valor antes → limpiar
                                  if (turningOn && !prev.incomeGrowthTouched) {
                                    newState.incomeAnnualGrowthRate = "";
                                  }

                                  return newState;
                                },
                                () => {

                                  this.triggerBlink("blinkSummary");
                                  this.triggerBlink("blinkTable");

                                  // Si está activado, manejar focos/cursor
                                  if (!this.state.constantValue) {
                                    const el = document.getElementById("incomeAnnualGrowthRate");
                                    if (!el) return;

                                    el.focus();

                                    const val = String(this.state.incomeAnnualGrowthRate || "");

                                    if (val === "") {
                                      // Caso 1: Primera activación → cursor al inicio
                                      el.setSelectionRange(0, 0);
                                    } else {
                                      // Caso 2: Ya tenía valor → cursor antes del punto
                                      const dotIndex = val.indexOf(".");
                                      const pos = dotIndex > -1 ? dotIndex : val.length;

                                      el.setSelectionRange(pos, pos);
                                    }
                                  }
                                }
                              );
                            }}
                            checked={!this.state.constantValue}
                          />


                          <span>
                            &nbsp; Incorporate Income Growth&nbsp;
                            <i
                              data-tip={`Income growth usually slows over time—about 3-6% early in your career, 1-3% mid-career, and near 0-1% later on. Include this in your plan to start smaller and increase savings as your income rises.`}
                              className="fa-info-circle-alt"
                            >
                              i
                            </i>
                          </span>
                        </div>
                        <div>
                          <Switch
                            
                            height={15}
                            width={30}
                            onColor={"#f1c40f"}
                            offColor={"#f1c40f"}
                            onChange={() => {
                              this.setState(
                                (prev) => {
                                  const next = !prev.includeInflation;
                                  const turningOn = !prev.includeInflation && next;

                                  const newState = {
                                    ...prev,
                                    includeInflation: next,
                                  };

                                  // Si se activa Y el usuario nunca ingresó inflación antes → dejar blank
                                  if (turningOn && !prev.inflationTouched) {
                                    newState.annualInflation = "";
                                  }

                                  return newState;
                                },
                                () => {

                                  this.triggerBlink("blinkSummary");
                                  this.triggerBlink("blinkTable");
                                  // Ahora manejar el focus
                                  if (this.state.includeInflation) {
                                    const el = document.getElementById("annualInflation");
                                    if (!el) return;

                                    el.focus();

                                    const val = String(this.state.annualInflation || "");

                                    if (val === "") {
                                      // PRIMERA VEZ → cursor al inicio
                                      el.setSelectionRange(0, 0);
                                    } else {
                                      // YA TENÍA VALOR → cursor al final (o antes del punto si quieres)
                                      el.setSelectionRange(val.length, val.length);
                                    }
                                  }

                                  this.calculateValues();
                                }
                              );
                            }}
                            checked={this.state.includeInflation}
                          />

                          <span>
                            &nbsp; Incorporate Inflation&nbsp;
                            <i
                              data-tip={
                                "Select this option to show values in nominal terms. Since the model uses a real return, the percentage of income you save stays the same. This feature distinguishes this model from most others."
                              }
                              className="fa-info-circle-alt"
                            >
                              i
                            </i>
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={9} style={{ fontSize: "1rem" }}>
                      <Col md={3}></Col>
                    </Col>
                  </Row>
                  <br />
                </div>
                <button
                  type="submit"
                  ref={this.submitCalculateForm}
                  hidden={true}
                />
              </Row>

              <Col id="mainResultsTable" md={12}></Col>
              <Col md={6} hidden={!this.state.dataReady}></Col>
              <Col
                md={12}
                hidden={!this.state.dataReady}
                className="mline simulation-results-section"
              >
                 <Row className="mline simulation-results-heading-row">
                  <Col xs="12">
                    <h4 className="simulation-results-heading">
                      {this.state.constantValue
                        ? "Simulation Results - No Income Growth"
                        : "Simulation Results - With and Without Income Growth"}
                    </h4>
                  </Col>
                </Row>
                <Col>
                </Col>
                <Row className={`mline simulation-results-content-row results-swap ${!this.state.constantValue ? "ig-on" : "ig-off"}`}>
                  <Col xs="12" md="6" className={`summary-col mb-3 mb-md-0`}>
                    {!this.state.isMobile ? (
                      <div className={`simulation-summary ${this.state.blinkSummary ? "fadein-soft" : ""}`}>
                        {!this.state.constantValue
                          ? this.getIncomeGrowthSummaryText()
                          : this.getNoIncomeGrowthSummary()}
                      </div>
                    ) : (
                      <div className={`simulation-summary mobile ${this.state.blinkSummary ? "fadein-soft" : ""}`}>
                        {this.state.constantValue
                          ? this.getNoIncomeGrowthSummaryMobile()
                          : this.showBeforeTableWithIGMobile()}
                      </div>
                    )}
                  </Col>
                  <Col xs="12" md="6" className={`table-col results-card `}>
                    <div className={`results-table-wrapper ${this.state.blinkTable ? "fadein-soft" : ""}`}>
                      {this.state.constantValue
                        ? this.renderNoIncomeGrowthTable()
                        : this.renderWithIncomeGrowthTable()}
                    </div>
                  </Col>
                </Row>
              

              <Row className="mline simulation-results-followup-row">
                <Col xs="12">
                  {!this.state.isMobile ? (
                    <div className="simulation-summary">{this.getCommonEvolutionText()}</div>
                  ) : (
                    <div className="simulation-summary mobile">
                      {this.state.constantValue ? "" : this.showAfterTableWithIGMobile()}
                    </div>
                  )}
                </Col>
            </Row>

              </Col>
              <div class="printableDiv" ref={(el) => (this.tableRef = el)}>
                {this.state.dataReady && (
                    <Row className="mline printable-results-divider simulation-results-printable-row">
                      <Col lg={{ size: 6 }} md={{ size: 12 }}>
                        <h5 className="subsection-title" style={{ textAlign: "left" }}>
                          Fund Will Be Fully Used
                        </h5>
                      </Col>

                      <Col lg={{ size: 6 }} md={{ size: 12 }} className="stacked-results-divider">
                        <h5 className="subsection-title" style={{ textAlign: "left" }}>
                          Fund Will Be Preserved
                        </h5>
                      </Col>
                    </Row>
                  )}

                {(this.state.constantValue
                  ? [""]
                  : [
                    this.state.incomeGrowthOrder ? "Growth" : "",
                    this.state.incomeGrowthOrder ? "" : "Growth",
                  ]
                ).map((type, index) => {
                  return (
                    <>
                      <Row
                        hidden={!this.state.dataReady}
                        className="mline printable-results-divider"
                        style={{
                          marginTop: this.state.constantValue
                            ? "-3rem"
                            : index === 0
                              ? "-1rem"
                              : "0.5rem"
                        }}
                      >
                        {perpetuityValues.map((param) => {
                          return (
                            <Col
                              key={"colo" + param}
                              lg={{ size: 6 }}
                              md={{ size: 12 }}
                              className={param ? "stacked-results-divider" : ""}
                            >
                              <h5 style={{ textAlign: "center" }}>
                                {type
                                  ? "With income growth"
                                  : (!this.state.constantValue ? "Without income growth ":"")}
                                &nbsp;
                                <i
                                  data-tip={
                                    param
                                      ? "After a number of withdrawal years the fund will be preserved."
                                      : "After a number of withdrawal years the fund will be fully used."
                                  }
                                  style={{display: this.state.constantValue? "none" : ""}}
                                  className="fa-info-circle-alt"
                                >
                                  i
                                </i>
                              </h5>

                              <Table
                                striped
                                bordered
                                hover
                                className="td_style center_content"
                               
                              >
                                <thead>
                                  <tr
                                    style={{
                                      textAlign: "center",
                                    }}
                                  >
                                    <td style={{ width: "10%" }}>YEAR</td>
                                    <td>BEG. BAL.</td>
                                    <td>RETURN</td>
                                    <td>SAVINGS</td>
                                    <td>WITHDRAWALS</td>
                                    <td>END. BAL.</td>
                                    <td>STATE</td>
                                  </tr>
                                </thead>
                                <ResultsTableBody
                                  saveYears={this.state.saveYears}
                                  pensionYears={this.state.pensionYears}
                                  isPerpetual={param}
                                  data={
                                    param
                                      ? this.state["detailedDataPermanent" + type]
                                      : this.state["detailedDataFungible" + type]
                                  }
                                  prevData={
                                    param
                                      ? this.state["prevDetailedPermanent" + type]
                                      : this.state["prevDetailedFungible" + type]
                                  }
                                  decimalScale={this.state.viewDecimals ? 2 : 0}
                                  showLess={this.state.showLess}
                                  counter={this.counter}
                                ></ResultsTableBody>
                              </Table>
                            </Col>
                          );
                        })}
                      </Row>
                    </>
                  );
                })}
              </div>
                
              <div hidden={!this.state.dataReady} className="">
                <Row>
                  <Col
                    md={12}
                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginRight: "1rem" }}>
                      <span
                        hidden={!this.state.dataReady}
                      >
                      <Switch
                        height={22}
                        width={40}
                        onColor={"#3498db"}
                        offColor={"#2980b9"}
                        onChange={() => this.toggleProp("showLess")}
                        checked={!this.state.showLess}
                      />
                      <span
                        style={{
                          verticalAlign: "super",
                        }}
                      >
                        {" "}
                        Show entire table
                      </span>
                    </span>

                    <span hidden={!this.state.dataReady}>
                      <Switch
                        height={22}
                        width={40}
                        onColor={"#f1c40f"}
                        offColor={"#f39c12"}
                        onChange={() => this.toggleProp("viewDecimals")}
                        checked={this.state.viewDecimals}
                      />
                      <span
                        style={{
                          verticalAlign: "super",
                        }}
                      >
                        {" "}
                        View Decimals
                      </span>
                    </span>

                    <span hidden={true || !this.state.dataReady}>
                      <Switch
                        height={22}
                        width={30}
                        offColor={"#FF0000"}
                        onChange={() => this.toggleProp("showNominal")}
                        checked={this.state.showNominal}
                      />
                      <span
                        style={{
                          verticalAlign: "super",
                        }}
                      >
                        {" "}
                        Show nominal value in graphic
                      </span>
                    </span>
                    </div>

                    <span hidden={!this.state.dataReady}>
                      <ReactToPrint
                        trigger={() => {
                          return (
                            <Button
                              data-tip="Print/Download"
                              color="success"
                              type="button"
                            >
                              Print tables
                            </Button>
                          );
                        }}
                        content={() => this.tableRef}
                      />
                    </span>
                  </Col>
                </Row>
                <br />
              </div>
              <Row>
                  <Col md={12}>
                  
                  </Col>
                </Row>

              <div
                id="chartsBlock"
                ref={(el) => (this.chartsPrintRef = el)}
                className="charts-block-print"
              >
                <Row>
                  <Col xs="12">
                    <h4 style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                      Simulation Graphs – Fund Path Over Time
                    </h4>
                  </Col>
                </Row>

                <Row className="charts-row">
                  <Col md={{ size: 6 }} className="charts-col">
                    {this.renderComparisonChart({
                      title: "Fund will be fully used",
                      baseData: this.state.chartDataFungibleBase || [],
                      growthData: this.state.chartDataFungibleGrowth || [],
                      sharedYMax,
                      showGrowth: !this.state.constantValue,
                    })}
                  </Col>

                  <Col md={{ size: 6 }} className="charts-col">
                    {this.renderComparisonChart({
                      title: "Fund will be preserved",
                      baseData: this.state.chartDataPermanentBase || [],
                      growthData: this.state.chartDataPermanentGrowth || [],
                      sharedYMax,
                      showGrowth: !this.state.constantValue,
                    })}
                  </Col>
                </Row>
              </div>
              <div className="charts-actions">
              <ReactToPrint
                trigger={() => (
                  <Button color="success" type="button">
                    Print charts
                  </Button>
                )}
                content={() => this.chartsPrintRef}
                pageStyle={`
                  @page { margin: 12mm; size: auto; }
                  @media print {
                    /* Evita saltos y elimina rellenos que empujan a otra página */
                    .charts-block-print, .charts-block-print * {
                      page-break-inside: avoid !important;
                      break-inside: avoid !important;
                    }
                    .charts-block-print {
                      margin: 0 !important;
                      padding: 0 !important;
                    }

                    /* Oculta tooltips/overlays de Recharts solo en papel */
                    .recharts-tooltip-wrapper,
                    .recharts-default-tooltip {
                      display: none !important;
                      visibility: hidden !important;
                      opacity: 0 !important;
                    }

                    html, body { height: auto !important; }
                  }
                `}
              />

            </div>
            </div>
          </div>
          <ReactTooltip className="tooltip" multiline={true} />
        </Form>
        {this.state.showInflationLimitModal && (
          <div className="modal-backdrop-custom">
            <div className="modal-custom">
              <h4>Maximum of 50% is allowed</h4>
              <button
                className="btn btn-primary"
                onClick={() => this.setState({ showInflationLimitModal: false })}
              >
                OK
              </button>
            </div>
          </div>
        )}

        
        <div className="bottomRight simulatorBottomRight">
          <ToggleSwitch title={this.state.reducedLineHeight ? "Reduced line height" : "Reduce line height"} value={this.state.reducedLineHeight} setter={this.setReducedLineHeight}></ToggleSwitch>
        </div>
      </div>
      
    );
  }
}

export default RetirementSimulator;
